import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

import {
  RhAvatar,
  RhButton,
  RhIcon,
  RhListContainer,
  RhListItem,
  RhPopover,
  RhPopoverMenu,
  RhPopoverToggle,
} from "@rhythm-ui/react";
import classNames from "classnames";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { AuthContext } from "@/context/AuthContext";
import { AbilityContext } from "@/context/Can";

import { USER_ROLE } from "@/constants";
import { zelevateWordLogoWhiteText } from "@/constants/media";
import mixPanelEventName from "@/constants/mixPanelEventName";

import { useScreenDetector } from "@/hooks/useScreenDetector";
import useTrackEvent from "@/hooks/useTrackEvent";

import AnalyticsLog from "@/helpers/AnalyticsLog";
import getFirstPermittedRoute from "@/helpers/getFirstPermittedRoute";

const NavBar = ({
  hideRoutes,
  hideAvatar,
}: {
  hideRoutes?: boolean;
  hideAvatar?: boolean;
}) => {
  const { auth } = useContext(AuthContext);

  const { isMobile: isShowMobileView } = useScreenDetector();

  return (
    <div className="bg-accent-1">
      <div className="flex h-t2xl w-full justify-between px-tnano py-tnano 2xl:container lg:px-tlg 2xl:mx-auto">
        <div className="flex h-full items-center">
          <Link href={auth.isAuthenticated ? "/login" : "/"}>
            <span className="flex items-center">
              <Image
                src={zelevateWordLogoWhiteText}
                layout="fixed"
                width={isShowMobileView ? "90px" : "146px"}
                height="30px"
                className="cursor-pointer"
              />
            </span>
          </Link>
        </div>
        {!auth.isAuthenticatedChecked ? (
          <></>
        ) : auth.isAuthenticated ? (
          <Links hideRoutes={hideRoutes} hideAvatar={hideAvatar} />
        ) : (
          <PublicLinks />
        )}
      </div>
    </div>
  );
};

export default NavBar;

const Links = ({
  hideRoutes,
  hideAvatar,
}: {
  hideRoutes?: boolean;
  hideAvatar?: boolean;
}) => {
  const router = useRouter();

  const ability = useContext(AbilityContext);

  // if the user is interviewer and candidate, they will be provided with a switcher to switch roles
  // and the routes will be accordingly shown based on the role selected
  // on first app load, the role selected will be according to the route which is being loaded first
  // as in, if first app load happens to be on /assessment (which is an interviewer route), the default role selected will be 'Interviewer'
  const { auth } = useContext(AuthContext);

  const [
    isCurrentSwitchCandidateOrInterviewer,
    setIsCurrentSwitchCandidateOrInterviewer,
  ] = useState<null | string>(null); // possible value: null | Interviewer | Candidate

  useEffect(() => {
    if (
      auth.roles.includes(USER_ROLE.INTERVIEWER) &&
      auth.roles.includes(USER_ROLE.CANDIDATE)
    ) {
      const currentPath = router.pathname;
      // check if current route is part of interviewer routes;
      const isCurrentPathBelongsToInterviewerRole = ALL_INTERVIEWER_ROUTES.map(
        (item) => item.path,
      ).includes(currentPath);

      if (isCurrentPathBelongsToInterviewerRole) {
        setIsCurrentSwitchCandidateOrInterviewer(USER_ROLE.INTERVIEWER);
      } else {
        setIsCurrentSwitchCandidateOrInterviewer(USER_ROLE.CANDIDATE);
      }
    } else if (auth.roles.includes(USER_ROLE.CANDIDATE)) {
      setIsCurrentSwitchCandidateOrInterviewer(USER_ROLE.CANDIDATE);
    }
  }, [auth.isAuthenticated]);

  const routesToRender = useMemo(() => {
    let routesForLoggedInUser = ALL_ROUTES;

    if (isCurrentSwitchCandidateOrInterviewer === USER_ROLE.INTERVIEWER)
      routesForLoggedInUser = ALL_INTERVIEWER_ROUTES;
    else if (isCurrentSwitchCandidateOrInterviewer === USER_ROLE.CANDIDATE)
      routesForLoggedInUser = ALL_CANDIDATE_ROUTES;

    return routesForLoggedInUser.filter((route) =>
      ability.can("RouteTo", route.path.replace("/", "")),
    );
  }, [isCurrentSwitchCandidateOrInterviewer, auth, router.isReady, ability]);

  return (
    <nav className="flex">
      {!hideRoutes &&
        routesToRender.map((route, idx) => (
          <button
            key={idx}
            className={classNames("hidden h-full items-center sm:flex", {
              "rounded bg-accent-2": router?.pathname.includes(route.path),
            })}
            data-testid="authenticated-nav-link-button"
          >
            <>
              {route.subMenu && route.subMenu?.length > 0 ? (
                <SubRoutes route={route} />
              ) : (
                <Link href={route.path} key={route.path}>
                  <span className={"w-[132px] cursor-pointer text-white"}>
                    {route.name}
                  </span>
                </Link>
              )}
            </>
          </button>
        ))}

      {!hideAvatar && (
        <UserProfile
          isCurrentSwitchCandidateOrInterviewer={
            isCurrentSwitchCandidateOrInterviewer
          }
        />
      )}
    </nav>
  );
};

const PublicLinks = () => {
  const router = useRouter();

  const publicRoutes = [
    {
      path: "/jobs",
      name: "Jobs",
    },
    {
      path: "/referral",
      name: "Refer & Earn",
    },
    {
      path: "/",
      name: "For Employer",
    },
  ];

  return (
    <nav className="hidden gap-tnano sm:flex">
      <div className="flex h-full">
        {publicRoutes.map((route, idx) => (
          <div
            key={idx}
            className={classNames("flex h-full items-center", {
              "rounded bg-accent-2": router?.pathname.includes(route.path),
            })}
          >
            <Link href={route.path} key={route.path}>
              <span
                className={"w-[132px] cursor-pointer text-center text-white"}
              >
                {route.name}
              </span>
            </Link>
          </div>
        ))}
      </div>
      <div className="flex gap-tsm">
        <Link href="/login" passHref>
          <RhButton className="" layout="outline">
            Login
          </RhButton>
        </Link>

        <Link href={"/signup"} passHref>
          <RhButton className="btn-bg"> Create Your Profile</RhButton>
        </Link>
      </div>
    </nav>
  );
};

const UserProfile = ({
  isCurrentSwitchCandidateOrInterviewer,
}: {
  isCurrentSwitchCandidateOrInterviewer: string | null;
}) => {
  const { auth } = useContext(AuthContext);
  const router = useRouter();

  const isUserCandidateAndInterviewer = useMemo(() => {
    return (
      auth.roles.includes(USER_ROLE.CANDIDATE) &&
      auth.roles.includes(USER_ROLE.INTERVIEWER)
    );
  }, [auth.isAuthenticated, auth.isAuthenticatedChecked]);

  const onLogoutHandler = () => {
    router.push("/signout");
  };

  const triggerEvent = useTrackEvent();

  const onRoleViewChange = (role: string) => {
    if (role === USER_ROLE.CANDIDATE) {
      // record event
      triggerEvent({
        name: mixPanelEventName.CLICK_ON_SWITCH_USER_ROLE_TO_CANDIDATE,
      });
    }
    const switchRoleDefaultRoute = getFirstPermittedRoute([role]);
    router.push(`/${switchRoleDefaultRoute}`);
  };

  return (
    <>
      {auth.isAuthenticated && (
        <div className="flex h-full items-center">
          <RhPopover>
            <RhPopoverToggle>
              <RhAvatar
                data-testid="profile-menu"
                name={auth.name}
                size={"sm"}
                className="ml-3 cursor-pointer"
              />
            </RhPopoverToggle>
            <RhPopoverMenu className="z-20">
              <RhListContainer className="border-1 w-[240px] overflow-auto rounded border-gray-400 bg-white shadow-[0_4px_8px_0px_rgba(0,0,0,0.24)]">
                {/* <RhListItem>{auth?.name}</RhListItem> */}
                {/* show for user who are both interviewer and candidate */}
                <div
                  className={`flex flex-col py-tpico [&>button>p]:text-start`}
                >
                  {isUserCandidateAndInterviewer &&
                    isCurrentSwitchCandidateOrInterviewer ===
                      USER_ROLE.INTERVIEWER && (
                      <RhListItem>
                        <RhButton
                          onClick={() => onRoleViewChange(USER_ROLE.CANDIDATE)}
                          layout="outline"
                        >
                          Switch as candidate
                        </RhButton>
                      </RhListItem>
                    )}
                  {isUserCandidateAndInterviewer &&
                    isCurrentSwitchCandidateOrInterviewer ===
                      USER_ROLE.CANDIDATE && (
                      <RhListItem>
                        <RhButton
                          layout="outline"
                          onClick={() =>
                            onRoleViewChange(USER_ROLE.INTERVIEWER)
                          }
                        >
                          Switch as interviewer
                        </RhButton>
                      </RhListItem>
                    )}
                  {/*  */}
                  {auth.roles.includes(USER_ROLE.CLIENT) && (
                    <>
                      <button
                        className={`cursor-pointer px-tsm py-tnano text-start hover:bg-primary-50`}
                        onClick={() => {
                          AnalyticsLog.track(
                            mixPanelEventName.MY_PROFILE_CLICK,
                          );
                          router.push("/recruit/profile");
                        }}
                        data-testid="my-profile-option"
                      >
                        My Profile
                      </button>

                      <button
                        className={`cursor-pointer px-tsm py-tnano text-start hover:bg-primary-50`}
                        onClick={() => router.push("/recruit/organization")}
                        data-testid="organization-option"
                      >
                        Organization
                      </button>
                    </>
                  )}

                  {auth.roles.includes(USER_ROLE.CANDIDATE) && (
                    <>
                      <button
                        className={`cursor-pointer px-tsm py-tnano hover:bg-primary-50`}
                        onClick={() => router.push("/jobs")}
                      >
                        <p> Jobs</p>
                      </button>
                      <button
                        className={`cursor-pointer px-tsm py-tnano hover:bg-primary-50`}
                        onClick={() => router.push("/candidate/profile")}
                      >
                        <p>Profile</p>
                      </button>
                    </>
                  )}

                  <button
                    className={`cursor-pointer px-tsm py-tnano hover:bg-primary-50`}
                    onClick={onLogoutHandler}
                  >
                    <p className="text-red-500" data-testid="log-out-option">
                      {" "}
                      Log out
                    </p>
                  </button>
                </div>
              </RhListContainer>
            </RhPopoverMenu>
          </RhPopover>
        </div>
      )}
    </>
  );
};

const SubRoutes = ({ route }: { route: RouteType }) => {
  const router = useRouter();

  return (
    <RhPopover
      placement="bottom-start"
      offset={[-12, 10]}
      className="bg-white dark:bg-dark-800"
    >
      <RhPopoverToggle>
        <div
          className={classNames(
            "mx-8 flex cursor-pointer items-center gap-tsm",
            {
              "text-primary-500":
                route.subMenu &&
                route.subMenu.some((menu) => menu.path === router.pathname),
              "text-white":
                route.subMenu &&
                !route.subMenu.some((menu) => menu.path === router.pathname),
            },
          )}
        >
          <span>{route.name}</span>
          <RhIcon icon="heroicons:chevron-down-solid" />
        </div>
      </RhPopoverToggle>
      <RhPopoverMenu className="z-10 bg-white">
        <RhListContainer className="relative w-56 overflow-auto shadow-md">
          {route.subMenu?.map((menu) => (
            <RhListItem key={menu.path}>
              <Link href={menu.path}>{menu.name}</Link>
            </RhListItem>
          ))}
        </RhListContainer>
      </RhPopoverMenu>
    </RhPopover>
  );
};

// routes

export interface RouteType {
  path: string;
  name: string;
  subMenu?: { path: string; name: string }[];
}

export const ALL_ROUTES: RouteType[] = [
  {
    path: "/dashboard",
    name: "Dashboard",
  },
  {
    path: "/jobs",
    name: "Jobs",
  },
  {
    path: "/candidates",
    name: "Candidates",
  },
  {
    path: "/interviewers",
    name: "Interviewers",
  },
  {
    path: "/companies",
    name: "Client",
  },
  {
    path: "/assessments",
    name: "Interviews",
    subMenu: [
      {
        path: "/assessments",
        name: "Interviews",
      },
      {
        path: "/interview-builder",
        name: "Interview Builder",
      },
      {
        path: "/interview-slots",
        name: "Interview Slots",
      },
    ],
  },
  {
    path: "/availability",
    name: "My Availability",
  },
  {
    path: "/candidate/profile",
    name: "Profile",
  },
  // client routes
  {
    path: "/recruit/jobs",
    name: "Jobs",
  },
];

// used for role switcher
export const ALL_CANDIDATE_ROUTES: RouteType[] = [
  {
    path: "/jobs",
    name: "Jobs",
  },
  {
    path: "/candidate/profile",
    name: "Profile",
  },
];

export const ALL_INTERVIEWER_ROUTES: RouteType[] = [
  {
    path: "/dashboard",
    name: "Dashboard",
  },
  {
    path: "/assessments",
    name: "Interviews",
  },
  {
    path: "/availability",
    name: "My Availability",
  },
];
